import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../../services/axiosClient";
import { updateProfile } from "../userSlice";
import { LoadingForType } from "./types";
/**
 * Constants for API version and path.
 */
const API_VERSION_PERSONA = "v1";
const API_PATH__PERSONA = "personas";
/**
 * Function to update default persona in user profile.
 * Dispatches an action to update the profile with the provided persona data.
 *
 * @param {object} params - Parameters for updating the user profile.
 * @param {AsyncThunkConfig} params.thunkAPI - The thunk API.
 * @param {IPersona} params.personaData - The persona data.
 */
const updateDefaultPersonaInUserProfile = async (params) => {
    if (params.personaData) {
        const { avatar_url, highlights, name, id } = params.personaData;
        await params.thunkAPI.dispatch(updateProfile({
            default_persona_id: id,
            name,
            avatar_url,
            highlights,
        }));
    }
};
/**
 * Thunk action to get all personas.
 * Makes an HTTP GET request to fetch the personas.
 */
export const getPersonas = createAsyncThunk("persona/get", async (_, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to create a new persona.
 * Makes an HTTP POST request to create the persona.
 *
 * @param {ICreatePersonaRequest} params - The request parameters for creating the persona.
 */
export const createPersona = createAsyncThunk("persona/create", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}`;
        const response = await axiosClient.post(apiUrl, params);
        // Dispatch action to update userInfo slice if created persona is default
        if (params.is_default) {
            updateDefaultPersonaInUserProfile({
                thunkAPI,
                personaData: response.data,
            });
        }
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to update an existing persona.
 * Makes an HTTP PATCH request to update the persona.
 *
 * @param {object} params - Parameters for updating the persona.
 * @param {string} params.id - The ID of the persona to update.
 * @param {IUpdatePersonaRequest} params.params - The request parameters for updating the persona.
 */
export const updatePersona = createAsyncThunk("persona/update", async ({ id, params }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}/${id}`;
        const response = await axiosClient.patch(apiUrl, params);
        if (params.is_default) {
            updateDefaultPersonaInUserProfile({
                thunkAPI,
                personaData: response.data,
            });
        }
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to delete personas.
 * Makes an HTTP POST request to delete the personas.
 *
 * @param {string[]} ids - The IDs of the personas to delete.
 */
export const deletePersonas = createAsyncThunk("persona/delete", async (ids, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}/delete`;
        await axiosClient.post(apiUrl, { user_persona_ids: ids });
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to make a persona the default.
 * Makes an HTTP PATCH request to set the persona as default.
 *
 * @param {string} id - The ID of the persona to make default.
 */
export const makeDefaultPersona = createAsyncThunk("persona/makeDefault", async (id, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}/default`;
        const response = await axiosClient.patch(apiUrl, {
            user_persona_id: id,
        });
        // @ts-expect-error
        const persona = thunkAPI
            .getState()
            .userPersona.personas.find((persona) => persona.id === id);
        await updateDefaultPersonaInUserProfile({
            thunkAPI,
            personaData: persona,
        });
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to get a persona by ID.
 * Makes an HTTP GET request to fetch the persona by ID.
 *
 * @param {string} id - The ID of the persona to fetch.
 */
export const getPersonaById = createAsyncThunk("persona/getById", async (id, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/${API_PATH__PERSONA}/${id}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Thunk action to update the persona ID in a conversation.
 * Makes an HTTP PATCH request to update the persona ID in a specific conversation.
 *
 * @param {object} params - Parameters for updating the persona ID in the conversation.
 * @param {string} params.personaId - The persona ID to update in the conversation.
 * @param {string} params.conversationId - The conversation ID to update.
 */
export const updatePersonaIdInConversation = createAsyncThunk("persona/updatePersonaIdInConversation", async ({ personaId, conversationId }, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_PERSONA)}/conversations/${conversationId}/user_persona`;
        const response = await axiosClient.patch(apiUrl, {
            user_persona_id: personaId,
        });
        return { rulebook: response.data?.result?.rulebook ?? null };
    }
    catch (err) {
        throw thunkAPI.rejectWithValue(err?.response?.data);
    }
});
/**
 * Initial state for the user persona slice.
 */
const initialState = {
    selectedPersona: null,
    personas: [],
    personaIdsMap: {},
    defaultPersonaId: null,
    getPersonaByIdResult: null,
    loading: null,
};
/**
 * Function to handle the pending state for asynchronous actions.
 *
 * @param {IUserPersonaState} state - The current state.
 * @param {LoadingForType} loadingType - The type of loading.
 */
const handlePending = (state, loadingType) => {
    state.loading = {
        loadingType,
        isLoading: true,
    };
};
/**
 * Function to handle the rejected state for asynchronous actions.
 *
 * @param {IUserPersonaState} state - The current state.
 * @param {LoadingForType} loadingType - The type of loading.
 */
const handleRejected = (state, loadingType) => {
    state.loading = {
        loadingType,
        isLoading: false,
    };
};
/**
 * Create a slice for user personas with initial state, reducers, and extra reducers.
 */
export const userPersonaSlice = createSlice({
    name: "persona",
    initialState: initialState,
    reducers: {
        /**
         * Set the list of personas.
         *
         * @param {IUserPersonaState} state - The current state.
         * @param {PayloadAction<IPersona[]>} action - The action containing the list of personas.
         */
        setPersonas: (state, action) => {
            state.personas = action.payload;
        },
        /**
         * Set the selected persona.
         *
         * @param {IUserPersonaState} state - The current state.
         * @param {PayloadAction<IPersona | null>} action - The action containing the selected persona.
         */
        setSelectedPersona: (state, action) => {
            state.selectedPersona = action.payload;
        },
        /**
         * Set the default persona ID.
         *
         * @param {IUserPersonaState} state - The current state.
         * @param {PayloadAction<string>} action - The action containing the default persona ID.
         */
        setDefaultPersonaId: (state, action) => {
            state.defaultPersonaId = action.payload;
        },
        updateDefaultPersona: (state, action) => {
            const personas = state?.personas || [];
            const defaultPersonaId = state.defaultPersonaId;
            if (personas.length && defaultPersonaId && Array.isArray(personas)) {
                state.personas = personas.map((persona) => {
                    if (persona.id === defaultPersonaId) {
                        const updatedPersona = { ...persona, ...action.payload };
                        state.personaIdsMap[defaultPersonaId] = updatedPersona;
                        return updatedPersona;
                    }
                    return persona;
                });
            }
        },
    },
    /**
     * Handle extra reducers for asynchronous actions.
     */
    extraReducers(builder) {
        builder
            .addCase(getPersonas.pending, (state) => {
            handlePending(state, LoadingForType.getPersona);
        })
            .addCase(getPersonas.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.getPersona,
            };
            state.personas = action.payload;
            state.personaIdsMap = Array.isArray(action.payload)
                ? action.payload.reduce((map, persona) => {
                    map[persona.id] = persona;
                    return map;
                }, {})
                : {};
        })
            .addCase(getPersonas.rejected, (state) => {
            handleRejected(state, LoadingForType.getPersona);
        })
            .addCase(createPersona.pending, (state) => {
            handlePending(state, LoadingForType.createPersona);
        })
            .addCase(createPersona.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.createPersona,
            };
            state.personas.push(action.payload);
            state.personaIdsMap[action.payload.id] = action.payload;
            if (action.payload.is_default) {
                state.defaultPersonaId = action.payload.id;
            }
        })
            .addCase(createPersona.rejected, (state) => {
            handleRejected(state, LoadingForType.createPersona);
        })
            .addCase(updatePersona.pending, (state) => {
            handlePending(state, LoadingForType.updatePersona);
        })
            .addCase(updatePersona.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.updatePersona,
            };
            if (state.personas.length) {
                const index = state.personas.findIndex((persona) => persona.id === action.payload.id);
                state.personas[index] = action.payload;
                state.personaIdsMap[action.payload.id] = action.payload;
            }
        })
            .addCase(updatePersona.rejected, (state) => {
            handleRejected(state, LoadingForType.updatePersona);
        })
            .addCase(deletePersonas.pending, (state) => {
            handlePending(state, LoadingForType.deletePersona);
        })
            .addCase(deletePersonas.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.deletePersona,
            };
            if (state.personas.length) {
                const ids = action.meta.arg;
                state.personas = state.personas.filter((persona) => !ids.includes(persona.id));
                state.personaIdsMap = Object.fromEntries(Object.entries(state.personaIdsMap).filter(([key]) => !ids.includes(key)));
            }
        })
            .addCase(deletePersonas.rejected, (state) => {
            handleRejected(state, LoadingForType.deletePersona);
        })
            .addCase(getPersonaById.pending, (state) => {
            handlePending(state, LoadingForType.getPersonaById);
        })
            .addCase(getPersonaById.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.getPersonaById,
            };
            state.getPersonaByIdResult = action.payload;
        })
            .addCase(getPersonaById.rejected, (state) => {
            handleRejected(state, LoadingForType.getPersonaById);
        })
            .addCase(makeDefaultPersona.pending, (state) => {
            handlePending(state, LoadingForType.setDefaultPersona);
        })
            .addCase(makeDefaultPersona.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.setDefaultPersona,
            };
            state.defaultPersonaId = action.meta.arg;
        })
            .addCase(makeDefaultPersona.rejected, (state) => {
            handleRejected(state, LoadingForType.setDefaultPersona);
        })
            .addCase(updatePersonaIdInConversation.pending, (state) => {
            handlePending(state, LoadingForType.changePersonaToChat);
        })
            .addCase(updatePersonaIdInConversation.fulfilled, (state, action) => {
            state.loading = {
                isLoading: false,
                loadingType: LoadingForType.changePersonaToChat,
            };
            state.selectedPersona =
                state.personas.find((persona) => persona.id === action.meta.arg.personaId) || null;
        })
            .addCase(updatePersonaIdInConversation.rejected, (state) => {
            handleRejected(state, LoadingForType.changePersonaToChat);
        });
    },
});
/**
 * Exporting actions and reducer from the user persona slice.
 */
export const { setSelectedPersona, setDefaultPersonaId, setPersonas, updateDefaultPersona, } = userPersonaSlice.actions;
export default userPersonaSlice.reducer;
